import { createI18n } from 'vue-i18n'
import type { I18n, I18nOptions, Composer } from 'vue-i18n'
import { getCurrentInstance, type InjectionKey } from 'vue'
import { I18nInjectionKey } from 'vue-i18n'

const i18nOptions: I18nOptions = {
  legacy: false,
  locale: 'english', // set locale
  fallbackLocale: 'english',
  messages: {}
}

const i18n: I18n = createI18n<false, typeof i18nOptions>(i18nOptions)

const getResourceMessages = (r: any) => r.default || r

const normalizeLanguage = (rawLang: string): string => {
  const lang = (rawLang || '').trim().toLowerCase().replace(/-/g, '_')
  const aliases: Record<string, string> = {
    english_us: 'english',
    en_us: 'english',
    en: 'english',
    de: 'german',
    germanic: 'german',
    fr: 'french',
    it: 'italian',
    es: 'spanish',
    ru: 'russian',
    zh: 'chinese',
    pt: 'portuguese',
    pt_pt: 'portuguese',
    pt_au: 'portuguese'
  }
  if (aliases[lang]) {
    return aliases[lang]
  }

  const baseLang = lang.split(/[-_]/)[0]
  return aliases[baseLang] || lang
}

export const loadTranslations = async function (lang: string) {
  ;(i18n.global as unknown as Composer).locale.value = lang
  const messages = await import(`@/locales/${lang}.json`).then(getResourceMessages)
  i18n.global.setLocaleMessage(lang, getResourceMessages(messages))
}

declare global {
  interface Window {
    wxlanguage: any
  }
}

export const initializeLang = async () => {
  const availableLanguages = [ 'english', 'french', 'german', 'italian', 'spanish', 'russian', 'chinese', 'portuguese' ];
  let initLang = typeof window.wxlanguage === 'undefined' ? 'english' : atob(window.wxlanguage)
  initLang = normalizeLanguage(initLang)
  if (availableLanguages.indexOf(initLang) < 0) {
    initLang = 'english'
  }
  await loadTranslations(initLang)
}

export default i18n

export function deepInject<T>(key: InjectionKey<T> | string): T | undefined {
  let inst = getCurrentInstance()
  if (inst === null) {
    throw new Error('getCurrentInstance returned null')
  }
  inst = inst.parent
  if (inst === null) {
    return
  }
  while (inst !== null) {
    // @ts-expect-error
    if (key in inst.provides) {
      // @ts-expect-error
      return inst.provides[key]
    }
    inst = inst.parent
  }
}

interface Options {
  messages?: Record<string, Record<string, string>>
}

//use this function for translation, nested child component doesnot work with useI18n provided by vue-i18n
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useI18n(options?: Options) {
  const instance = deepInject(I18nInjectionKey)
  if (instance === undefined) {
    throw new Error('i18n not found in context')
  }
  const { global } = instance

  // merge locale messages
  const messages = options?.messages ?? {}
  const locales = Object.keys(messages)
  if (locales.length > 0) {
    locales.forEach((locale) => {
      global.mergeLocaleMessage(locale, messages[locale])
    })
  }
  // make locale messages reactive
  return global
}



export const getLocalLanguage = () => {
    const availableLanguages = ['english','french','german','italian','spanish','russian','chinese','portuguese'];
    let initLang = (typeof window.wxlanguage === 'undefined') ? 'english' : atob(window.wxlanguage);
    initLang = normalizeLanguage(initLang);
    if(availableLanguages.indexOf(initLang) < 0) {
        initLang = 'english';
    }
    return initLang;
}

export const getLocalLanguageISOCode = () => {
    const lang = getLocalLanguage();
    const langMapping: Record<string, string> = {
        'english': 'en',
        'french': 'fr',
        'german': 'de',
        'italian': 'it',
        'spanish': 'es',
        'russian': 'ru',
        'chinese': 'zh',
        'portuguese': 'pt'
    };
    return langMapping[lang] || 'en';
}
